import { createContext, useContext, useMemo } from 'react';

/**
 * Discovery's URLs. Every screen has one, so a refresh keeps your place and a
 * link to a folder tab can be pasted into chat.
 *
 *   {base}                               folders home
 *   {base}/folders/:folderId/:tab        a folder (tab defaults to leads)
 *   {base}/creators | suggestions | settings | outreach
 *   {base}/repeat-runs?folder=&schedule=&run=   scheduled searches across folders
 *
 * Email is its own page in the portal's Sourcing nav, mounted apart from
 * Discovery (see EmailApp):
 *
 *   {emailBase}?tab=audience|campaigns           who can be emailed, and campaigns
 *   {emailBase}/campaigns/:publicId              one campaign's results
 */
export const FOLDER_TABS = ['leads', 'find', 'schedules', 'briefing', 'activity'] as const;
export type FolderTab = (typeof FOLDER_TABS)[number];

export const FOLDER_TAB_LABELS: Record<FolderTab, string> = {
  leads: 'Leads',
  find: 'Find creators',
  schedules: 'Schedules',
  briefing: 'Briefing',
  activity: 'Activity',
};

export function parseFolderTab(raw: string | undefined | null): FolderTab | null {
  return (FOLDER_TABS as readonly string[]).includes(raw ?? '') ? (raw as FolderTab) : null;
}

export const DEFAULT_DISCOVERY_BASE = '/admin/discovery';

export const DEFAULT_EMAIL_BASE = '/admin/email';

export const DiscoveryBaseContext = createContext<string>(DEFAULT_DISCOVERY_BASE);
export const EmailBaseContext = createContext<string>(DEFAULT_EMAIL_BASE);

export interface DiscoveryPaths {
  base: string;
  home: string;
  folder: (publicId: string, tab?: FolderTab) => string;
  creators: string;
  /** Every scheduled search; optionally filtered to a folder, with one schedule's history open. */
  repeatRuns: (opts?: { folder?: string; schedule?: string }) => string;
  suggestions: string;
  settings: string;
  outreach: string;
  email: (tab?: 'audience' | 'campaigns') => string;
  emailCampaign: (publicId: string) => string;
}

export function buildPaths(base: string, emailBase: string = DEFAULT_EMAIL_BASE): DiscoveryPaths {
  const b = base.replace(/\/+$/, '');
  const e = emailBase.replace(/\/+$/, '');
  return {
    base: b,
    home: b,
    folder: (publicId, tab = 'leads') => `${b}/folders/${encodeURIComponent(publicId)}/${tab}`,
    creators: `${b}/creators`,
    repeatRuns: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts.folder) q.set('folder', opts.folder);
      if (opts.schedule) q.set('schedule', opts.schedule);
      const qs = q.toString();
      return qs ? `${b}/repeat-runs?${qs}` : `${b}/repeat-runs`;
    },
    suggestions: `${b}/suggestions`,
    settings: `${b}/settings`,
    outreach: `${b}/outreach`,
    email: (tab) => (tab && tab !== 'audience' ? `${e}?tab=${tab}` : e),
    emailCampaign: (publicId) => `${e}/campaigns/${encodeURIComponent(publicId)}`,
  };
}

export function useDiscoveryPaths(): DiscoveryPaths {
  const base = useContext(DiscoveryBaseContext);
  const emailBase = useContext(EmailBaseContext);
  return useMemo(() => buildPaths(base, emailBase), [base, emailBase]);
}
